import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import styles from "@/styles/form.module.css";
import {useGetClub, useSaveClub} from "./clubHooks";
import type {Club} from '@iziping/common';
import { useNotification } from '@iziping/ui';
import {useTranslation} from "react-i18next";
import type {ApiError} from '@iziping/common';

interface ClubFormValues {
    name: string;
    number?: string;
    department: number;
}

const ClubPage: React.FC = () => {
    const { id } = useParams();
    const clubId = id ? parseInt(id, 10) : undefined;
    const navigate = useNavigate();
    const {t} = useTranslation();
    const {notify} = useNotification();

    const { data: club, isLoading, isError } = useGetClub(clubId);
    const saveMutation = useSaveClub();

    const { register, handleSubmit, reset, formState: { errors } } = useForm<ClubFormValues>({
        defaultValues: { name: '', number: '', department: 0 }
    });

    useEffect(() => {
        if (club) {
            reset({name: club.name, number: club.number, department: club.department,});
        } else {
            reset({ name: '', number: '', department: 0 });
        }
    }, [club, reset]);

    if (isLoading) return <div>Chargement...</div>;
    if (isError) return <div>Erreur lors du chargement du club</div>;

    const onSubmit = async (data: ClubFormValues) => {
        try {
            const payload: Club | ClubFormValues = club ? { ...data, id: club.id } : data;
            const saved = await saveMutation.mutateAsync(payload);
            notify(t('club.created.success'), 'success');
            navigate('/clubs');
        } catch (err: unknown) {
            const apiError = err as ApiError;
            notify(t(apiError.error) || 'Erreur inconnue', 'error');
        }
    };

    return (
        <form className={styles.formContainer} onSubmit={handleSubmit(onSubmit)}>
            <h2 className={styles.title}>{club ? t('club.update') : t('club.create')}</h2>

            <div className={styles.formGroup}>
                <label className={styles.label} htmlFor="name">{t('club.name')}</label>
                <input
                    className={styles.input}
                    id="name"
                    {...register('name', {
                        required: {
                            value: true,
                            message: t("club.name.required")
                        }
                    })}
                />
                {errors.name && <span className={styles.error}>{errors.name.message}</span>}
            </div>

            <div className={styles.formGroup}>
                <label className={styles.label} htmlFor="number">{t('club.number')}</label>
                <input
                    className={styles.input}
                    id="number"
                    {...register('number')}
                />
            </div>

            <div className={styles.formGroup}>
                <label className={styles.label} htmlFor="department">{t('club.department')}</label>
                <input
                    className={styles.input}
                    id="department"
                    type="number"
                    {...register('department', {
                        required: {
                            value: true,
                            message: t("club.department.required")
                        },
                        min: { value: 1, message: 'Doit être supérieur à 0' }
                    })}
                />
                {errors.department && <span className={styles.error}>{errors.department.message}</span>}
            </div>

            <button type="submit" className={styles.btnPrimary}>
                {club ? t('club.update.button') : t('club.create.button')}
            </button>
        </form>
    );
};

export default ClubPage;